/**
 * Matched accept · hint · size presets for FileDropZone surfaces (LF-4771).
 *
 * A drop zone has three independent knobs that are easy to let drift apart:
 * the native picker filter (`accept`), the visible types/size line (`hintText`),
 * and the enforced client cap (`maxSizeBytes`). Author them together here and
 * have each call site spread ONE preset, so a surface can never advertise one
 * thing and accept another.
 *
 * **Size is standardized on `MAX_FILE_SIZE` (20 MB)** — the only server-grounded
 * cap in the system: it is the portal-intake default and the global frontend
 * limit. The authenticated `/files/pre-signed-url` → S3 PUT enforces no size at
 * all (the presigned PUT carries no content-length condition; upload-complete
 * only rejects 0-byte files), so these caps are purely client-side UX. The one
 * justified exception is 50 MB for wills in the portal — the `clientIntakeUpload`
 * preset below (LF-5791).
 */

import {
  MAX_FILE_SIZE,
  MAX_FILE_SIZE_MB,
  MAX_FILE_SIZE_WILL,
  MAX_FILE_SIZE_WILL_MB
} from '@/lib/constants/file-limits';

export interface FileAcceptPreset {
  /** Native `<input accept>` string — the picker filter + drop validation. */
  accept: string;
  /** Human-readable types/size line shown in the drop surface. */
  hintText: string;
  /** Client-side size ceiling in bytes. */
  maxSizeBytes: number;
}

const DOCX =
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document';

/**
 * The registry. Spread a preset onto `<FileDropZone {...preset} />` and feed the
 * same `accept`/`maxSizeBytes` to `useFileDropZoneState`. Add a new key here
 * (never a one-off literal at a call site) when a surface needs a new combo.
 */
export const FILE_DROP_ZONE_ACCEPT = {
  /**
   * Supporting documents — PDF / Word / images. Used by Intake Context and the
   * Scribe session Context tab (both feed the AI summary). `hintText` size is
   * derived from `MAX_FILE_SIZE_MB` so the displayed number and the enforced
   * cap stay in lockstep.
   */
  documents: {
    accept: `application/pdf,${DOCX},image/jpeg,image/png,image/heic,image/heif`,
    /*
      **Plain language, not a format list.** The native picker's `accept` filter
      already greys out anything unsupported and a drag-drop of the wrong thing
      raises a validation error naming the problem, so enumerating the types
      mostly informs people who cannot act on the information. `HEIC` was the
      clearest case: it is what every iPhone photo actually is, and it is the one
      entry in the list nobody recognises as describing their own file.

      The size cap stays, because that one IS actionable — a 30 MB scan can be
      re-exported smaller. It is derived from `MAX_FILE_SIZE_MB` so the number
      shown and the number enforced cannot drift.
    */
    hintText: `Photos or documents, up to ${MAX_FILE_SIZE_MB} MB`,
    maxSizeBytes: MAX_FILE_SIZE
  },
  /**
   * Identity documents (primary / secondary ID) — a PDF or a photo. Used by the
   * contacts ID-upload surfaces. Size standardized on `MAX_FILE_SIZE` (20 MB) to
   * match those surfaces' current behaviour; the per-fileType registry's older
   * 5 MB value is the stricter alternative if we later want to tighten (review
   * call — flagged in LF-4854 / plan `[FE-8]`).
   *
   * Also accepts a forwarded Outlook email (.eml/.msg, LF-6125) — the backend
   * unwraps it into its attachments and checks THOSE against this same slot's
   * allowed types, so an email carrying a licence photo satisfies this ID slot
   * exactly as a directly-uploaded photo would.
   */
  identityDocument: {
    accept:
      'application/pdf,image/jpeg,image/png,message/rfc822,application/vnd.ms-outlook,.eml,.msg',
    hintText: `PDF, JPG, PNG, or a forwarded email (.eml/.msg), up to ${MAX_FILE_SIZE_MB} MB`,
    maxSizeBytes: MAX_FILE_SIZE
  },
  /**
   * A wet-ink document scanned back in — the signed copy of a declaration
   * collected offline. PDF, or a forwarded email (.eml/.msg, LF-6125) whose
   * unwrapped attachment is itself a PDF — see `identityDocument` above.
   */
  signedDocument: {
    accept:
      'application/pdf,message/rfc822,application/vnd.ms-outlook,.eml,.msg',
    hintText: `PDF, or a forwarded email (.eml/.msg), up to ${MAX_FILE_SIZE_MB} MB`,
    maxSizeBytes: MAX_FILE_SIZE
  },
  /**
   * Client-intake portal — the executor's "additional documents" upload on the
   * end-of-intake confirmation page (LF-5791). Public magic-link surface: PDF or
   * images (HEIC/HEIF included — transcoded to JPEG server-side). Sized at 50 MB
   * to match the portal's `documentType: 'will'` backend cap (`MAX_FILE_SIZE_WILL`).
   * `accept` is extension-based (not MIME) because HEIC files frequently report
   * an empty `file.type`, so an extension match is the reliable picker/drop filter.
   *
   * Also accepts a forwarded email (.eml/.msg, LF-6125) — unwrapped server-side
   * into its attachments — but ONLY on this generic "additional documents" slot,
   * never on the portal's death-certificate/ID/will slots (see
   * `PortalIntakeFormService.requestUpload`'s documentType gate).
   */
  clientIntakeUpload: {
    accept: '.pdf,.jpg,.jpeg,.png,.heic,.heif,.eml,.msg',
    hintText: `PDF, JPG, PNG, HEIC, or a forwarded email (.eml/.msg), up to ${MAX_FILE_SIZE_WILL_MB} MB`,
    maxSizeBytes: MAX_FILE_SIZE_WILL
  },
  /**
   * A Word document the backend will render as an **ad-hoc template** — the
   * lead cost agreement's upload door (LF-6012 / LEADS-45).
   *
   * `.docx` alone, and that is a hard requirement rather than a preference: the
   * document goes through docxtemplater so its `<<signatureBlock>>` resolves
   * into the invisible anchors Annature searches the PDF text layer for. A PDF
   * would have nothing to render and no anchor to place a signature on, so
   * offering one here would only move the refusal to the server.
   */
  wordTemplate: {
    accept: DOCX,
    hintText: `Word (.docx), up to ${MAX_FILE_SIZE_MB} MB`,
    maxSizeBytes: MAX_FILE_SIZE
  }
} satisfies Record<string, FileAcceptPreset>;
